/*
 * brand-reveal.js — Bethsotérica cross-browser scroll-reveal + hero parallax.
 *
 * Self-contained ES module (no custom element, no dependencies). Replaces the
 * Chromium-only `@supports (animation-timeline: view())` reveal with a JS-driven
 * IntersectionObserver pattern that works in Safari/Firefox/Chrome.
 *
 * Progressive enhancement: without JS, `.brand-reveal-ready` is never added, so
 * the CSS hidden state never applies and all content is fully visible.
 *
 * CWV-safe: animates opacity/transform only (CLS = 0). Reveal stagger is handled
 * in CSS (transition-delay per --2/--3/--4 modifier), so this stays tiny.
 * Loaded as `type="module" fetchpriority="low"`, gated per-template in scripts.liquid.
 *
 * WCAG: every motion path is suppressed under prefers-reduced-motion (CSS is the
 * authoritative guard; JS also checks matchMedia).
 */
(function () {
  'use strict';

  // Idempotency guard — this block can be requested by multiple gated templates.
  if (window.__brandRevealInit) return;
  window.__brandRevealInit = true;

  var reduceMotion = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

  // Flag the document so the CSS hidden state only applies when JS is running.
  document.documentElement.classList.add('brand-reveal-ready');

  /* ── Scroll-reveal ─────────────────────────────────────────────────────── */
  function initReveal() {
    var targets = document.querySelectorAll('.brand-reveal');
    if (!targets.length) return;

    // Reduced motion or no IntersectionObserver → reveal everything immediately.
    if (reduceMotion || !('IntersectionObserver' in window)) {
      for (var i = 0; i < targets.length; i++) targets[i].classList.add('is-visible');
      return;
    }

    var observer = new IntersectionObserver(function (entries, obs) {
      for (var j = 0; j < entries.length; j++) {
        var entry = entries[j];
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          obs.unobserve(entry.target);
        }
      }
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

    for (var k = 0; k < targets.length; k++) observer.observe(targets[k]);
  }

  /* ── Parallax: hero atmosphere ([data-parallax]) + product cards ───────────
     Subtle, transform-only (CLS-safe, NEVER opacity → LCP-safe) vertical
     parallax. Fully disabled under reduced-motion AND on coarse/no-hover
     pointers (touch) — the listener is never even wired in those cases.
     Hero atmosphere uses [data-parallax] (per-element factor, default 0.15,
     max 40px). Product cards (<product-card> custom elements) get a much
     smaller factor (0.06) + smaller clamp (14px) so above-the-fold cards stay
     painted and grids never thrash. rAF-throttled + passive.
     update() batches ALL getBoundingClientRect READS (phase 1) before ANY
     .style.transform WRITES (phase 2) to avoid layout thrash on card grids. */
  function initParallax() {
    var atmos = document.querySelectorAll('[data-parallax]');
    var cards = document.querySelectorAll('product-card');
    if (!atmos.length && !cards.length) return;

    // Disable on reduced-motion or coarse/no-hover (touch) devices.
    var coarse = window.matchMedia
      ? window.matchMedia('(hover: none), (pointer: coarse)').matches
      : false;
    if (reduceMotion || coarse) return;

    var HERO_MAX = 40; // px — hero decorative layer clamp.
    var CARD_MAX = 14; // px — product-card clamp (small, conservative).

    var items = [];
    var i;
    // Hero atmosphere: factor from attribute (default 0.15), larger clamp.
    for (i = 0; i < atmos.length; i++) {
      var factor = parseFloat(atmos[i].getAttribute('data-parallax'));
      if (isNaN(factor)) factor = 0.15;
      items.push({ el: atmos[i], factor: factor, max: HERO_MAX, offset: null });
    }
    // Product cards: small fixed factor + small clamp.
    for (i = 0; i < cards.length; i++) {
      items.push({ el: cards[i], factor: 0.06, max: CARD_MAX, offset: null });
    }

    var ticking = false;

    function update() {
      ticking = false;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var viewportCenter = vh / 2;
      var j;

      // ── PHASE 1: READ ── all getBoundingClientRect first; compute + stash
      // the target offset (null = offscreen → skip the write). No .style here.
      for (j = 0; j < items.length; j++) {
        var item = items[j];
        var rect = item.el.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > vh) {
          item.offset = null; // fully offscreen → leave untouched in phase 2
          continue;
        }
        var elCenter = rect.top + rect.height / 2;
        var off = (elCenter - viewportCenter) * item.factor * -1;
        if (off > item.max) off = item.max;
        if (off < -item.max) off = -item.max;
        item.offset = off;
      }

      // ── PHASE 2: WRITE ── all .style.transform after every read is done.
      for (j = 0; j < items.length; j++) {
        var w = items[j];
        if (w.offset === null) continue; // offscreen → leave last value untouched
        w.el.style.transform = 'translate3d(0, ' + w.offset.toFixed(1) + 'px, 0)';
      }
    }

    function onScroll() {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update(); // initial position
  }

  /* ── Init ──────────────────────────────────────────────────────────────── */
  function init() {
    initReveal();
    initParallax();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
